import React, { useEffect, useRef, useState } from 'react';
import { listCanvasLibrary, createCanvasLibrary, updateCanvasLibrary, deleteCanvasLibrary } from '../lib/adminApi';
import { CANVAS_REGISTRY, getCanvasDefinition } from '../canvases/registry';
import CanvasStage from '../canvases/CanvasStage';
import ConfirmDialog from '../components/ConfirmDialog';

const EMPTY_FORM = { moving_object_type: '', label: '', description: '' };

function moveObjLabel(key) {
  return CANVAS_REGISTRY.find((c) => c.key === key)?.label.replace(/\s*\(.*\)$/, '') || key;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function CanvasPreview({ canvas, photoOverride }) {
  const def = getCanvasDefinition(canvas.moving_object_type);
  const backgroundImageUrl = photoOverride || canvas.background_image_url || undefined;
  if (!def) {
    return <div className="canvas-lib-preview" />;
  }
  return (
    <div className="canvas-lib-preview">
      <CanvasStage
        key={`${canvas.id || canvas.moving_object_type}-${backgroundImageUrl || 'none'}`}
        createStage={def.createStage}
        messages={[]}
        backgroundImageUrl={backgroundImageUrl}
        interactive={false}
      />
    </div>
  );
}

export default function CanvasLibrary() {
  const [view, setView] = useState('list'); // 'list' | 'form' | 'detail'
  const [canvases, setCanvases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    listCanvasLibrary()
      .then((data) => setCanvases(data.canvases || []))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = { ...form };
      if (photoFile) payload.background_image_base64 = await fileToBase64(photoFile);
      await createCanvasLibrary(payload);
      resetForm();
      setView('list');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = (canvas) => {
    setSelectedId(canvas.id);
    setEditing(false);
    setError('');
    setForm({
      moving_object_type: canvas.moving_object_type,
      label: canvas.label,
      description: canvas.description || '',
    });
    setPhotoFile(null);
    setPhotoPreview(canvas.background_image_url || null);
    setView('detail');
  };

  const handleSaveEdits = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const payload = { ...form };
      if (photoFile) payload.background_image_base64 = await fileToBase64(photoFile);
      await updateCanvasLibrary(selectedId, payload);
      setEditing(false);
      setView('list');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deleteCanvasLibrary(target.id);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (view === 'form' || view === 'detail') {
    const isDetail = view === 'detail';
    const readOnly = isDetail && !editing;

    return (
      <div>
        <button className="admin-back-link" onClick={() => { setView('list'); resetForm(); }}>
          Back to Canvas Library Homepage
        </button>
        <div className="admin-page-header">
          <div>
            <h1>{isDetail ? form.label : 'New Canvas'}</h1>
            <p>A background photo paired with a moving object — used across Projects, Services and the live canvas.</p>
          </div>
          {isDetail && (
            <button
              className="admin-btn admin-btn-primary"
              onClick={(e) => (editing ? handleSaveEdits(e) : setEditing(true))}
              disabled={saving}
            >
              {editing ? (saving ? 'Saving…' : 'Save Edits') : 'Edit'}
            </button>
          )}
        </div>

        <div className="admin-card">
          {error && <div className="admin-alert admin-alert-error">{error}</div>}
          <form className="admin-form" onSubmit={isDetail ? handleSaveEdits : handleCreate}>
            <div className="admin-form-row">
              <div className="admin-field">
                <label>Label *</label>
                <input required disabled={readOnly} value={form.label} onChange={handleChange('label')} />
              </div>
              <div className="admin-field">
                <label>Moving Object *</label>
                <select required disabled={readOnly} value={form.moving_object_type} onChange={handleChange('moving_object_type')}>
                  <option value="">Select a moving object…</option>
                  {CANVAS_REGISTRY.map((c) => (
                    <option key={c.key} value={c.key}>{moveObjLabel(c.key)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="admin-field">
              <label>Description</label>
              <textarea
                rows={3} disabled={readOnly}
                value={form.description}
                onChange={handleChange('description')}
                placeholder="Shown wherever this canvas is picked — editing this updates it everywhere instantly."
              />
            </div>

            <div className="admin-field">
              <label>Background Photo</label>
              {!readOnly && (
                <input type="file" accept="image/png,image/jpeg,image/webp" ref={fileInputRef} onChange={handleFile} />
              )}
              <span className="admin-hint">JPEG, PNG or WebP, up to 5MB. Leave empty to use the built-in background.</span>
              {form.moving_object_type && (
                <div style={{ marginTop: 10 }}>
                  <CanvasPreview
                    canvas={{
                      id: selectedId || 'new-canvas',
                      moving_object_type: form.moving_object_type,
                      background_image_url: photoPreview || undefined,
                    }}
                    photoOverride={photoPreview || undefined}
                  />
                </div>
              )}
            </div>

            {!isDetail && (
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={() => { setView('list'); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1>Canvas Library</h1>
          <p>Background photos paired with a moving object. Add new ones, or edit a description to update it everywhere instantly.</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => { resetForm(); setView('form'); }}>+ New Canvas</button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {!loading && (
        <div className="canvas-lib-grid">
          {canvases.map((c) => (
            <div key={c.id} className="canvas-lib-card" onClick={() => openDetail(c)}>
              <CanvasPreview canvas={c} />
              <div className="canvas-lib-card-body">
                <div className="canvas-lib-card-header">
                  <h2>{c.label}</h2>
                  {c.background_image_url && <span className="admin-badge admin-badge-photo">Photo</span>}
                </div>
                <span className="admin-badge admin-badge-pending">{moveObjLabel(c.moving_object_type)}</span>
                {c.description && <p>{c.description}</p>}
                <div className="canvas-lib-card-actions">
                  <button
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={(e) => { e.stopPropagation(); openDetail(c); }}
                  >
                    Edit
                  </button>
                  <button
                    className="admin-btn admin-btn-danger admin-btn-sm"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(c); }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && canvases.length === 0 && <div className="admin-empty">No canvases in the library yet.</div>}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete canvas?"
        message="Are you sure you want to delete this canvas?"
        confirmLabel="Yes"
        cancelLabel="No"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
